import { useNavigate } from 'react-router-dom';
import { Brain, Heart } from 'lucide-react';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { useAuthStore } from '../../store/authStore';

const cardBase = 'rounded-card border border-v-outline bg-v-surface p-5 shadow-[0_1px_3px_rgb(0_0_0/0.05)]';

/**
 * Primer uso: en lugar de cuatro contadores en cero, un unico paso.
 *
 * Un contador en cero no dice que hacer, solo empuja a pensar que no se ha
 * hecho nada. Aqui hay un CTA y dos alternativas, sin cifras, sin porcentaje y
 * sin copy de culpa.
 */
export function PrimerUso() {
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const user = useAuthStore((s) => s.user);
  const nombre = user?.nombre?.trim().split(/\s+/)[0] || user?.nombre || '';

  const alternativas = [
    { flag: 'evaluaciones', icono: Brain, titulo: 'Hacer la evaluación' },
  ].filter((a) => isEnabled(a.flag));

  return (
    <div className="mx-auto w-full max-w-md space-y-4 md:max-w-3xl">
      <div
        className="relative overflow-hidden rounded-b-[1.75rem] px-5 pb-7 pt-6"
        style={{ backgroundImage: 'linear-gradient(135deg, #00574C 0%, #134E7B 100%)' }}
      >
        <div
          aria-hidden
          className="absolute -right-10 -top-14 h-48 w-48 rounded-full bg-white/20 blur-2xl"
        />
        <div className="relative">
          <p className="text-sm font-medium text-white/85">Tu espacio de bienestar</p>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-white">
            Hola, {nombre} 👋
          </h1>
          <p className="mt-1.5 text-sm text-white/90">Empieza hoy con 3 minutos.</p>
        </div>
      </div>

      <section className={cardBase} aria-labelledby="titulo-primer-paso">
        <span
          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold"
          style={{ backgroundColor: '#B8F5EC', color: '#003B34' }}
        >
          Tu primer paso
        </span>

        <h2 id="titulo-primer-paso" className="mt-3 text-xl font-extrabold leading-snug text-v-on-surface">
          Nada más, solo hoy.
        </h2>
        <p className="mt-1 text-sm text-v-on-surface-variant">
          Tres minutos de respiración para empezar. Puedes dejarlo para mañana si quieres.
        </p>

        {/* CTA en mint SOLIDO con tinta oscura, no el degradado de marca con
            blanco: sobre #00BFA6 el blanco da 2.33:1 y a 16px negrita hacen
            falta 4.5:1. #003B34 sobre #00BFA6 da 5.37:1, y devuelve el color
            vibrante justo donde esta la accion. */}
        <button
          type="button"
          onClick={() => navigate('/ejercicios')}
          className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-pill
                     px-5 text-base font-bold text-[#003B34] transition-transform active:scale-[0.98]"
          style={{ backgroundColor: '#00BFA6' }}
        >
          <Heart className="h-5 w-5" aria-hidden />
          Respirar 3 min
        </button>

        {alternativas.length > 0 && (
          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-v-on-surface-variant">
              También puedes…
            </p>
            <ul className="mt-2 space-y-2">
              {alternativas.map(({ flag, icono: Icono, titulo }) => (
                <li key={flag}>
                  <button
                    type="button"
                    onClick={() => navigate('/evaluacion')}
                    className="flex min-h-12 w-full items-center gap-3 rounded-card border
                               border-v-outline px-4 text-left transition-colors active:bg-black/5"
                  >
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                      style={{ backgroundColor: '#DBEAFE' }}
                    >
                      <Icono className="h-5 w-5" style={{ color: '#1E40AF' }} aria-hidden />
                    </span>
                    <span className="text-sm font-semibold text-v-on-surface">{titulo}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
